/**
 * The ONE allowlist and the ONE redirect policy for "fetch a document URL the caller already holds".
 *
 * Two routes need it — `pages/api/convert-document.ts` (Office → HTML) and `pages/api/document-bytes.ts`
 * (the byte proxy a browser needs when a media host refuses it CORS) — and an SSRF gate kept in two copies
 * is one copy that gets the next fix and one that does not. It lives here rather than in either route so
 * neither imports the other's handler.
 *
 * Pure, no Node APIs: it is server-side by use, not by capability, which is also what lets the whole gate be
 * tested exhaustively (`test/convert-document-route.test.ts`).
 */

/** The hosts a Wix media URL can legitimately live on. THE security control of both routes — everything
 *  else there is a size or time budget. Suffix-matched against the parsed hostname, never a substring of
 *  the raw URL, so `https://evil.com/?x=.wixstatic.com` cannot pass.
 *
 *  `.wixel.com` is the design platform's own family: `links.wixel.com` serves the asset bytes and other
 *  subdomains serve pages, so the family is allowed rather than the one asset host — the subdomains move,
 *  and what this list defends is SSRF, which a Wix-owned domain does not weaken. Every entry keeps its
 *  leading dot, so `notwixel.com` still fails. */
export const ALLOWED_HOSTS = ['.wixstatic.com', '.usrfiles.com', '.wixmp.com', '.filesusr.com', '.wixel.com'];

/** Hops allowed per document. Wixel needs one; more than a couple is a loop, not a CDN. */
export const MAX_REDIRECTS = 3;

/** 20 MB. Big enough for a real document, deck or PDF, bounded so one request cannot cost the pod an
 *  arbitrary amount of memory. The client's `MAX_PDF_PREVIEW_BYTES` is deliberately the same number. */
export const MAX_DOCUMENT_BYTES = 20 * 1024 * 1024;

export const DOCUMENT_FETCH_TIMEOUT_MS = 20_000;

export function isAllowedDocumentHost(rawUrl: string): boolean {
  let url: URL;
  try {
    url = new URL(rawUrl);
  } catch {
    return false;
  }
  if (url.protocol !== 'https:') return false;
  const host = url.hostname.toLowerCase();
  return ALLOWED_HOSTS.some((suffix) => host.endsWith(suffix));
}

/** A hop off the allowlist — the request is refused, and the URL is NOT echoed back (a rejection message is
 *  not a place to reflect caller input). */
export class HostNotAllowedError extends Error {
  constructor() {
    super('The document host is not on the allowlist');
    this.name = 'HostNotAllowedError';
  }
}

export class TooManyRedirectsError extends Error {
  constructor() {
    super(`More than ${MAX_REDIRECTS} redirects`);
    this.name = 'TooManyRedirectsError';
  }
}

/** A redirect with no `Location` — the upstream is broken, not the caller. */
export class MissingLocationError extends Error {
  constructor(readonly status: number) {
    super(`Redirect ${status} carried no Location`);
    this.name = 'MissingLocationError';
  }
}

/**
 * Fetches `url`, following redirects BY HAND and re-checking the host on every hop.
 *
 * Letting `fetch` follow them applies the allowlist to the first URL only and lets undici request the
 * `Location` unchecked, so the security property these routes claim would hold only as long as every
 * allowed host never redirected off the list. That is not hypothetical: `links.wixel.com/link/<id>/raw`
 * answers 302 to the wixmp object holding the bytes, so redirects are part of the normal path rather than
 * an edge case.
 *
 * No `Referer` and no cookies: a Wix media URL is token-signed, and the URL we came from is itself signed.
 */
export async function fetchAllowedDocument(url: string, signal?: AbortSignal): Promise<Response> {
  let target = url;
  for (let hop = 0; hop <= MAX_REDIRECTS; hop++) {
    if (!isAllowedDocumentHost(target)) throw new HostNotAllowedError();
    const hopResponse = await fetch(target, { signal, redirect: 'manual', referrerPolicy: 'no-referrer' });
    if (hopResponse.status < 300 || hopResponse.status >= 400) return hopResponse;
    const location = hopResponse.headers.get('location');
    if (!location) throw new MissingLocationError(hopResponse.status);
    // Resolved against the URL that answered, since a Location may legitimately be relative.
    target = new URL(location, target).toString();
  }
  throw new TooManyRedirectsError();
}

/** Reads at most `maxBytes`, aborting the transfer on the chunk that crosses the cap. Refuses rather than
 *  truncating — half a PDF is not a document — mirroring `readBoundedBytes` on the client side. */
export async function readBoundedBody(res: Response, maxBytes: number): Promise<Uint8Array> {
  const reader = res.body?.getReader?.();
  if (!reader) {
    const buffer = new Uint8Array(await res.arrayBuffer());
    if (buffer.byteLength > maxBytes) throw new PayloadTooLargeError(buffer.byteLength);
    return buffer;
  }
  const chunks: Uint8Array[] = [];
  let bytes = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    if (!value) continue;
    bytes += value.byteLength;
    if (bytes > maxBytes) {
      void reader.cancel();
      throw new PayloadTooLargeError(bytes);
    }
    chunks.push(value);
  }
  const all = new Uint8Array(bytes);
  let at = 0;
  for (const chunk of chunks) {
    all.set(chunk, at);
    at += chunk.byteLength;
  }
  return all;
}

export class PayloadTooLargeError extends Error {
  constructor(readonly bytes: number) {
    super(`Document is ${bytes} bytes, over the ${MAX_DOCUMENT_BYTES} byte cap`);
    this.name = 'PayloadTooLargeError';
  }
}
